import React from 'react';
import styled from 'styled-components';
import { Content } from 'mxp-schemas';
import { generatePath } from 'react-router-dom';
import { Grid, Container, MenuItem } from 'semantic-ui-react';
import { ContentSideFilterContainer } from 'containers/ContentSideFilterContainer';
import { CombinedFiltersPaginationViewContainer } from 'containers/CombinedFiltersPaginationViewContainer';
import { NotFound } from 'components/App/lazy-imports';
import { NoResults } from 'components/molecules/NoResults/NoResults';
import { AggregationHero } from 'components/organisms/AggregationHero';
import { Footer } from 'components/molecules/Footer';
import { OnlyMobileCSS } from 'components/atoms';
import { PageHelmet } from 'components/atoms/PageHelmet/PageHelmet';
import { HeaderPageWrapper } from 'components/templates/HeaderPageWrapper';
import { getPath } from 'utils';
import { getShortcutLabel } from 'utils/getShortcutLabel';
import { Routes, PaginationInt, sortByOptions, SortByKeyNames } from 'constants/index';
import { useSelector } from 'react-redux';
import { topicsHashSelector } from 'modules/topics/selectors';
import { industriesHashSelector } from 'modules/industries/selectors';
import { trendsHashSelector } from 'modules/trends/selectors';
import { skillsHashSelector } from 'modules/skills/selectors';

const createTabPane = (handleTabClick: any, categorySlug: Content.CategorySlugs, key: string, text: string) => {
  return {
    categorySlug,
    menuItem: (
      <MenuItem key={key} data-testid={`mi-${categorySlug}`}>
        {text}
      </MenuItem>
    ),
    render: () => null,
    onClick: () => handleTabClick(categorySlug),
  };
};

export interface Props {
  isAuth: boolean | null;
  isSubtopicPage: boolean;
  isPremiumContentPage: boolean;
  sortBy: string;
  page: number;
  perPage: number;
  filtersStr: string; // subscription required to fire fetchSearchContent if changed
  pagination: PaginationInt;
  topicId: string;
  topicName: string;
  topicSlug: string;
  subtopicId: string;
  subtopicName: string;
  subtopicSlug: string;
  subskillName: string;
  searchTopicSlug: string;
  industrySlug: string;
  trendSlug: string;
  skillSlug: string;
  categorySlug: string;
  categoryName: string;
  categoryDescription: string;
  resultsLoading: boolean;
  resultsFetched: boolean;
  results: State.FeedCardContentItem[];
  hasShortcut: boolean;
  fetchSearchContent: ({ isGridView }: { isGridView: boolean }) => Promise<void>;
  navigate: (path: string) => void;
  setSkipUserInfoRefresh: (skip: boolean) => void;
  resetSearchModule: () => void;
  toggleLoginReload(shouldReload: boolean): void;
  resetSkipSearchFrontloadRequest: () => void;
  skipSearchFrontloadRequest: boolean;
}

export const PageCategoryAggregation: React.FC<Props> = ({
  isSubtopicPage,
  isPremiumContentPage,
  sortBy,
  page,
  perPage,
  pagination,
  topicId,
  topicName,
  topicSlug,
  subtopicId,
  subtopicName,
  subtopicSlug,
  subskillName,
  searchTopicSlug,
  industrySlug,
  trendSlug,
  skillSlug,
  categorySlug,
  categoryName,
  categoryDescription,
  resultsLoading,
  resultsFetched,
  results,
  hasShortcut,
  navigate,
  resetSearchModule,
  toggleLoginReload,
  setSkipUserInfoRefresh,
}) => {
  const subTaxonomyName = subtopicName || subskillName;
  const isNoResults: boolean = Boolean(resultsFetched && !resultsLoading && results.length === 0);
  const sortByOptionItems = [
    sortByOptions[SortByKeyNames.NEWEST_FIRST],
    sortByOptions[SortByKeyNames.OLDEST_FIRST],
    sortByOptions[SortByKeyNames.ALPHABETICAL],
  ];

  const allTopics: any = useSelector(state => topicsHashSelector(state as State.Root));
  const allIndustries: any = useSelector(state => industriesHashSelector(state as State.Root));
  const allTrends: any = useSelector(state => trendsHashSelector(state as State.Root));
  const allSkills: any = useSelector(state => skillsHashSelector(state as State.Root));

  const addTaxonomyType = (taxonomyArray: any[], taxonomyType: string) => {
    const typedTaxonomyArray = taxonomyArray.map((item: any) => {
      return { ...item, type: taxonomyType };
    });
    return typedTaxonomyArray;
  };

  const typedTopics = addTaxonomyType(Object.values(allTopics), 'topic');
  const typedIndustries = addTaxonomyType(Object.values(allIndustries), 'industry');
  const typedSkills = addTaxonomyType(Object.values(allSkills), 'skill');
  const typedTrends = addTaxonomyType(Object.values(allTrends), 'trend');

  const allTaxonomies = typedTopics.concat(typedIndustries, typedSkills, typedTrends);

  const findTaxonomy = (type: any, slug: string): any => {
    return Object.values(type).filter((item: any) => {
      return item.slug && slug && item.slug === slug;
    })[0];
  };

  const primaryTaxonomy = findTaxonomy(allTaxonomies, topicSlug);
  const titleArray = [
    findTaxonomy(allTaxonomies, searchTopicSlug),
    findTaxonomy(allTaxonomies, industrySlug),
    findTaxonomy(allTaxonomies, trendSlug),
    findTaxonomy(allTaxonomies, skillSlug),
  ].filter(item => item);

  const targetContainer: any = React.useRef();
  const containerContentRef: any = React.useRef();

  React.useEffect(() => resetSearchModule, [resetSearchModule]);
  React.useEffect(() => {
    if (isPremiumContentPage && resultsFetched) {
      setSkipUserInfoRefresh(true);
    }
    const clean = () => setSkipUserInfoRefresh(false);
    return clean;
  }, [isPremiumContentPage, resultsFetched, setSkipUserInfoRefresh]);

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const toggleModalOpen = React.useCallback(() => setModalOpen(!modalOpen), [modalOpen, setModalOpen]);
  const handleTabClick = React.useCallback(
    (nextCategorySlug: string, e?: React.SyntheticEvent<HTMLElement>) => {
      if (e) e.preventDefault();

      const generatePathClick = (route: Routes) =>
        navigate(
          generatePath(getPath(route), {
            categorySlug: nextCategorySlug,
            topicSlug: topicSlug || undefined,
            subtopicSlug: subtopicSlug || undefined,
          })
        );

      isPremiumContentPage
        ? generatePathClick(Routes.PREMIUM_CONTENT_AGGS_PAGE)
        : generatePathClick(Routes.CATEGORY_AGGS_PAGE_CIMA);
    },
    [topicSlug, subtopicSlug, isPremiumContentPage, navigate]
  );

  const handleToggleLoginReload = (shouldReload: boolean) => {
    toggleLoginReload(shouldReload);
  };

  const panes: Common.PageCategoryAggregationHeroTabPane[] = React.useMemo(
    () => [
      createTabPane(handleTabClick, Content.CategorySlugs.ALL, '0', 'All'),
      createTabPane(handleTabClick, Content.CategorySlugs.RESOURCES, '1', 'Resources'),
      createTabPane(handleTabClick, Content.CategorySlugs.NEWS, '2', 'News'),
      createTabPane(handleTabClick, Content.CategorySlugs.ADVOCACY, '3', 'Advocacy'),
      createTabPane(handleTabClick, Content.CategorySlugs.PROFESSIONAL_INSIGHTS, '4', 'Professional Insights'),
      createTabPane(handleTabClick, Content.CategorySlugs.CPE_LEARNING, '5', 'CPE & Learning'),
    ],
    [handleTabClick]
  );

  if (
    (subtopicSlug && !subTaxonomyName) ||
    (primaryTaxonomy?.slug && !primaryTaxonomy?.name && topicSlug && !topicName)
  ) {
    return <NotFound />;
  }

  let title: string;

  if (titleArray.length === 2) {
    title = `${titleArray[0].name} and ${titleArray[1].name}`;
  } else {
    const primaryTitle = topicName || primaryTaxonomy?.name || '';
    const titleJoiner = topicName && subTaxonomyName ? ':' : '';
    const secondaryTitle = subTaxonomyName || '';
    const categoryTitle = categorySlug === Content.CategorySlugs.ALL ? `${categoryName} Content` : categoryName;
    title = `${primaryTitle}${titleJoiner}${secondaryTitle} | ${categoryTitle}`.replace(/^(\||\s)+/g, '');
  }

  const metaDescription: string = categoryDescription
    .replace('[topic/subtopic/section]', subTaxonomyName || topicName || '')
    .replace(/^(\||\s)+/g, '');

  return (
    <div>
      {/* PageTitle component was overwritten */}
      <PageHelmet
        title={title}
        meta={{
          title: `${title} | AICPA & CIMA`,
          description: metaDescription,
        }}
        openGraph={{
          title: getShortcutLabel({
            categorySlug,
            categoryName,
            topicName,
            subtopicName: subTaxonomyName,
          }),
          description: metaDescription,
          passUrl: true,
        }}
        usePagination
      />
      <OnlyMobileCSS>
        <ContentSideFilterContainer
          isModalOpen={modalOpen}
          closeModal={toggleModalOpen}
          resultsNumber={pagination.total}
          resultsFetched={resultsFetched}
          hideCategoryFilter
          hideTopicFilter
          hideSubtopicFilter={isSubtopicPage}
        />
      </OnlyMobileCSS>
      <HeaderPageWrapper contentRef={targetContainer} contentInnerRef={containerContentRef}>
        <AggregationHero
          topicId={topicId ? topicId : primaryTaxonomy?.id}
          topicSlug={topicSlug ? topicSlug : primaryTaxonomy?.slug}
          topicName={topicName ? topicName : primaryTaxonomy?.name}
          taxonomyType={primaryTaxonomy?.type ? primaryTaxonomy?.type : 'topic'}
          slicedTaxonomies={titleArray.length === 2 ? titleArray : null}
          subtopicId={subtopicId}
          subtopicSlug={subtopicSlug}
          subtopicName={subTaxonomyName}
          categorySlug={categorySlug}
          hasShortcut={hasShortcut}
          isSubtopicPage={isSubtopicPage}
          hideBackButton={isPremiumContentPage}
          panes={panes}
          showCopyLinkButton={Boolean(!topicSlug && !subtopicSlug)}
          toggleLoginReload={handleToggleLoginReload}
        />
        {isNoResults ? (
          <StyledContainer>
            <Grid>
              <Grid.Column computer={4} tablet={16} />
              <Grid.Column computer={12} tablet={16}>
                <NoResults subtopicName={subTaxonomyName} topicName={topicName} categoryName={categoryName} />
              </Grid.Column>
            </Grid>
          </StyledContainer>
        ) : (
          <StyledWrapper>
            <CombinedFiltersPaginationViewContainer
              resultsFetched={resultsFetched}
              sortByOptionItems={sortByOptionItems}
              sortBy={sortBy}
              page={page}
              pagination={pagination}
              results={results}
              toggleModalOpen={toggleModalOpen}
              resultsLoading={resultsLoading}
              perPage={perPage}
              isGridView
              topicName={topicName}
              topicSlug={topicSlug}
              isCategoryPage
              hideCategoryFilter
              isSubtopicPage={isSubtopicPage}
            />
          </StyledWrapper>
        )}
        <Footer />
      </HeaderPageWrapper>
    </div>
  );
};

const StyledContainer = styled(Container)`
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    min-height: calc(100vh - ${props => props.theme.pxToRem(170)});
  }
  position: relative;
  top: ${props => props.theme.pxToRem(20)};
`;

const StyledWrapper = styled.div`
  padding-top: ${props => props.theme.pxToRem(40)};
`;
